import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import { Variable } from '~/packages/variable/variables.typing'
import { dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import prefixProperties from '~/prix/utils/prefixProperties'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'
import appSebraeAttendanceLocationStatsAction from './appSebraeAttendanceLocationStats.action.json' assert { type: 'json' }
import { pick } from '~/prix/utils/types/object'
import cnaeDivision from '~/packages/cnaeDivision/cnaeDivision.entity'

const attendanceDataShape = object({
  remoteJustification: string().isOptional().label('Justificativa remota'),
  geolocationAccuracy: number().isOptional().label('Precisão da geolocalização'),
})

const registrationStatusOptions = {
  active: 'Ativo',
  inapt: 'Inapto',
  suspended: 'Suspenso',
  extinct: 'Extinto',
}

const sizeOptions = {
  MEI: 'MEI',
  ME: 'ME',
  EPP: 'EPP',
  other: 'Outros',
  unknown: 'Desconhecido',
}

const ageGroupOptions = {
  startup: 'Startup',
  young: 'Jovem',
  growth: 'Crescimento',
  established: 'Estabelecida',
}

const attendanceLocationShape = object({
  attendanceId: string().label('ID do atendimento'),
  agentCpf: string().label('CPF do agente'),
  agentName: string().isOptional().label('Nome do agente'),
  agentPolygonCpf: string().label('Polígono do agente'),
  startDate: dateTime().label('Data de início do atendimento'),
  segmentId: string().label('Identificador do segmento'),
  segmentDescription: string().isOptional().label('Descrição do segmento'),
  size: enumerated(sizeOptions).label('Porte da empresa'),
  ageGroup: enumerated(ageGroupOptions).label('Maturidade da empresa'),
  registrationStatus: enumerated(registrationStatusOptions).label('Status'),
  pointOnStreet: object({
    type: string().label('Tipo'),
    coordinates: object({
      0: number().label('Longitude'),
      1: number().label('Latitude'),
    }),
    registrationStatus: enumerated(registrationStatusOptions).label('Status'),
    size: enumerated(sizeOptions).label('Porte'),
    ageGroup: enumerated(ageGroupOptions).label('Maturidade'),
  }).label('Localização do atendimento'),

  ...prefixProperties('attendanceData.', attendanceDataShape.shape),
  ...prefixProperties('cnaeDivision.', cnaeDivision.schema.shape),
})

export const appSebraeAttendanceLocationExpression = expression(attendanceLocationShape.shape)

export const appSebraeAttendanceLocationVariable: Variable = {
  key: 'appSebraeAttendanceLocation',
  categoryKey: 'appSebraeAttendance',
  categoryKeys: ['appSebraeAttendance'],
  actionDefinitionKey: appSebraeAttendanceLocationStatsAction.key,
  title: 'Geolocalização registrada pelo agente',
  label: 'Atendimentos de Agentes SEBRAE Na Sua Empresa',
  description:
    'Distribui os atendimentos pela geolocalização registrada pelo agente no momento do atendimento.',
  unitSingular: 'atendimento',
  unitPlural: 'atendimentos',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: attendanceLocationShape,
  betterDensity: 'more',
  valueRules: {
    minValue: 0,
  },
  relevance: 1,
  payload: {
    productName: 'Agentes SEBRAE Na Sua Empresa',
    portfolioName: ['App SEBRAE'],
  },
  basicSelect: ['attendanceId', 'agentCpf', 'startDate'],
  detailedSelect: [
    'attendanceId',
    'agentCpf',
    'agentName',
    'startDate',
    'attendanceData.remoteJustification',
    'attendanceData.geolocationAccuracy',
  ],
  exportSortBy: { key: 'startDate', direction: 'desc' },
  filterableBy: expression(
    pick(appSebraeAttendanceLocationExpression.shape, [
      'size',
      'ageGroup',
      'segmentId',
      'registrationStatus',
      'agentCpf',
      'agentPolygonCpf',
    ]),
  ),
  compatibleChildrenTypes: [
    'country',
    'macroRegion',
    'state',
    'mesoRegion',
    'microRegion',
    'city',
    'neighborhood',
    'street',
    'item',
  ],
  spaceAnalysis: undefined,
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [...defaultDetectionLayers],
  },
  assertions: prepareAssertions(appSebraeAttendanceLocationExpression),
}

const appSebraeAttendanceLocationStatsDefinition = defineAction({
  ...appSebraeAttendanceLocationStatsAction,
  input: object({
    variable,
    highlight: appSebraeAttendanceLocationExpression.isOptional(),
    filter: appSebraeAttendanceLocationExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForIncidence().isOptional(),
    select: select(attendanceLocationShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      numerator: number().isOptional(),
      denominator: number().isOptional(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
      h3: string().isOptional(),
      dateTime: string().isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default appSebraeAttendanceLocationStatsDefinition
